import { useRef, useState } from "react";
import { UploadCloud, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadChunked, fmtBytes } from "@/lib/files";
import { cn } from "@/lib/utils";

export function UploadZone({ workspace, onUploaded, disabled }) {
  const [queue, setQueue] = useState([]);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const patch = (id, p) => setQueue((q) => q.map((x) => (x.id === id ? { ...x, ...p } : x)));

  const start = async (list) => {
    for (const file of Array.from(list || [])) {
      if (!file.size) { toast.error(`"${file.name}" is empty, Sir.`); continue; }
      const id = `${Date.now()}-${file.name}`;
      setQueue((q) => [...q, { id, name: file.name, size: file.size, progress: 0, status: "uploading" }]);
      try {
        const rec = await uploadChunked(file, { workspace, onProgress: (p) => patch(id, { progress: p }) });
        patch(id, { status: "done", progress: 100 });
        onUploaded?.(rec);
        setTimeout(() => setQueue((q) => q.filter((x) => x.id !== id)), 2500);
      } catch (e) {
        patch(id, { status: "error", error: e.message || "Upload failed" });
      }
    }
  };

  return (
    <div className="space-y-2" data-testid="upload-zone">
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (!disabled) start(e.dataTransfer.files); }}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button" tabIndex={0} aria-disabled={disabled}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !disabled && inputRef.current?.click()}
        data-testid="upload-dropzone"
        className={cn("hud-chamfer border border-dashed px-5 py-6 flex items-center gap-4 cursor-pointer transition-colors select-none",
          disabled ? "border-white/10 opacity-50 cursor-not-allowed" :
            drag ? "border-jarvis-cyan bg-jarvis-cyan/10" : "border-jarvis-cyan/30 bg-jarvis-cyan/[0.03] hover:border-jarvis-cyan/60 hover:bg-jarvis-cyan/[0.06]")}>
        <div className="w-12 h-12 shrink-0 flex items-center justify-center border border-jarvis-cyan/30 text-jarvis-cyan hud-chamfer">
          <UploadCloud className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-brand text-[12px] font-semibold uppercase tracking-[0.16em] text-jarvis-cyanBright">
            {disabled ? "Storage offline" : "Drop files or media here"}
          </div>
          <div className="font-ui text-[13px] text-jarvis-cyanBright/50 mt-0.5">
            {disabled ? "Object storage is not configured." : "Images, video, audio, documents · up to 200 MB each · uploaded in encrypted chunks"}
          </div>
        </div>
        <input ref={inputRef} type="file" multiple hidden data-testid="upload-input"
          onChange={(e) => { start(e.target.files); e.target.value = ""; }} />
      </div>

      {queue.length > 0 && (
        <ul className="space-y-1.5" data-testid="upload-queue">
          {queue.map((q) => (
            <li key={q.id} className="flex items-center gap-3 px-3 py-2 border border-jarvis-cyan/15 bg-black/30" data-testid="upload-queue-item">
              {q.status === "uploading" && <Loader2 className="w-3.5 h-3.5 text-jarvis-cyan animate-spin shrink-0" />}
              {q.status === "done" && <CheckCircle2 className="w-3.5 h-3.5 text-jarvis-green shrink-0" />}
              {q.status === "error" && <XCircle className="w-3.5 h-3.5 text-jarvis-red shrink-0" />}
              <span className="font-ui text-[13px] text-slate-200 truncate flex-1">{q.name}</span>
              <span className="font-num text-[11px] text-slate-500 shrink-0">{fmtBytes(q.size)}</span>
              <div className="w-28 h-1.5 bg-white/5 shrink-0 relative overflow-hidden">
                <div className={cn("absolute inset-y-0 left-0 transition-[width]", q.status === "error" ? "bg-jarvis-red" : "bg-jarvis-cyan")}
                  style={{ width: `${q.progress}%`, boxShadow: "0 0 8px rgba(24,201,255,0.6)" }} />
              </div>
              <span className={cn("font-num text-[11px] w-10 text-right shrink-0", q.status === "error" ? "text-jarvis-red" : "text-jarvis-cyan")}>
                {q.status === "error" ? "ERR" : `${q.progress}%`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
